/**
 * Draft validation: the deterministic gate every UI (web, embed, native) shares,
 * so "is this event exportable?" has one answer instead of one per surface.
 */

import { LIMITS } from './sanitize';
import {
  DATE_PATTERN,
  DATE_TIME_PATTERN,
  type EventDraft,
  type EventPerson,
  type EventTime,
  timeClock,
  timeDate,
} from './model';

export type EventProblemCode =
  | 'title-missing'
  | 'start-invalid'
  | 'end-invalid'
  | 'end-kind-mismatch'
  | 'end-before-start'
  | 'email-invalid'
  | 'url-invalid'
  | 'reminder-invalid'
  | 'recurrence-invalid'
  | 'too-long'
  | 'too-many';

export interface EventProblem {
  /** Draft path the problem belongs to, e.g. `end` or `attendees[1].email`. */
  readonly field: string;
  readonly code: EventProblemCode;
  readonly message: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function timeIsValid(time: EventTime): boolean {
  return time.kind === 'date'
    ? DATE_PATTERN.test(time.date)
    : DATE_TIME_PATTERN.test(time.dateTime);
}

/** Sortable key for two times of the same kind expressed in the same zone. */
function sortKey(time: EventTime): string {
  return `${timeDate(time)}T${timeClock(time) ?? '00:00:00'}`;
}

function personProblems(person: EventPerson, field: string): EventProblem[] {
  return EMAIL_PATTERN.test(person.email)
    ? []
    : [
        {
          field: `${field}.email`,
          code: 'email-invalid',
          message: `"${person.email}" is not an email address.`,
        },
      ];
}

/** Every problem with a draft, in draft order. An empty list means it can be exported. */
/**
 * Whether two times can be compared at all. Ordering two wall clocks in different zones needs a
 * tz database to turn them into instants, and this package deliberately has none — so a flight
 * that lands at 11:00 in New York having left at 15:00 in London is left alone rather than
 * reported as ending before it started.
 */
function sameZone(start: EventTime, end: EventTime): boolean {
  const zoneOf = (time: EventTime): string | undefined =>
    time.kind === 'date' ? undefined : time.timeZone;
  return zoneOf(start) === zoneOf(end);
}

export function validateEvent(draft: EventDraft): EventProblem[] {
  const problems: EventProblem[] = [];

  if (draft.title.trim() === '') {
    problems.push({ field: 'title', code: 'title-missing', message: 'An event needs a title.' });
  }

  // Length caps are a user-visible rule, not just a safety valve: the generators clamp
  // silently (see sanitize.ts), so without this a long paste would be truncated unexplained.
  const tooLong = (field: 'title' | 'location' | 'description' | 'url', limit: number): void => {
    const value = draft[field];
    if (typeof value === 'string' && value.length > limit) {
      problems.push({
        field,
        code: 'too-long',
        message: `Keep the ${field} under ${limit} characters.`,
      });
    }
  };
  tooLong('title', LIMITS.title);
  tooLong('location', LIMITS.location);
  tooLong('description', LIMITS.description);
  tooLong('url', LIMITS.url);

  if ((draft.attendees ?? []).length > LIMITS.attendees) {
    problems.push({
      field: 'attendees',
      code: 'too-many',
      message: `An event can carry up to ${LIMITS.attendees} guests.`,
    });
  }
  if ((draft.remindersMinutesBefore ?? []).length > LIMITS.reminders) {
    problems.push({
      field: 'remindersMinutesBefore',
      code: 'too-many',
      message: `An event can carry up to ${LIMITS.reminders} reminders.`,
    });
  }

  const startValid = timeIsValid(draft.start);
  if (!startValid) {
    problems.push({
      field: 'start',
      code: 'start-invalid',
      message: 'Start is not a valid date or time.',
    });
  }

  if (draft.end !== undefined) {
    const end = draft.end;
    if (!timeIsValid(end)) {
      problems.push({
        field: 'end',
        code: 'end-invalid',
        message: 'End is not a valid date or time.',
      });
    } else if (end.kind !== draft.start.kind) {
      problems.push({
        field: 'end',
        code: 'end-kind-mismatch',
        message: 'An event is either all-day or timed — start and end must match.',
      });
    } else if (startValid && sameZone(draft.start, end) && sortKey(end) < sortKey(draft.start)) {
      problems.push({ field: 'end', code: 'end-before-start', message: 'End is before start.' });
    }
  }

  if (draft.organizer) problems.push(...personProblems(draft.organizer, 'organizer'));
  (draft.attendees ?? []).forEach((attendee, index) => {
    problems.push(...personProblems(attendee, `attendees[${index}]`));
  });

  if (draft.url !== undefined && draft.url !== '' && !/^https?:\/\/\S+$/.test(draft.url)) {
    problems.push({
      field: 'url',
      code: 'url-invalid',
      message: 'URL must start with http:// or https://.',
    });
  }

  (draft.remindersMinutesBefore ?? []).forEach((minutes, index) => {
    if (!Number.isInteger(minutes) || minutes < 0) {
      problems.push({
        field: `remindersMinutesBefore[${index}]`,
        code: 'reminder-invalid',
        message: 'A reminder is a whole number of minutes before the event.',
      });
    }
  });

  const recurrence = draft.recurrence;
  if (recurrence) {
    const { interval, count, until } = recurrence;
    if (interval !== undefined && (!Number.isInteger(interval) || interval < 1)) {
      problems.push({
        field: 'recurrence.interval',
        code: 'recurrence-invalid',
        message: 'Repeat interval must be 1 or more.',
      });
    }
    if (count !== undefined && until !== undefined) {
      problems.push({
        field: 'recurrence',
        code: 'recurrence-invalid',
        message: 'A repeat ends after a number of times or on a date, not both.',
      });
    }
    if (count !== undefined && Number.isInteger(count) && count > LIMITS.recurrenceCount) {
      problems.push({
        field: 'recurrence.count',
        code: 'too-many',
        message: `A repeat can run at most ${LIMITS.recurrenceCount} times.`,
      });
    }
    if (count !== undefined && (!Number.isInteger(count) || count < 1)) {
      problems.push({
        field: 'recurrence.count',
        code: 'recurrence-invalid',
        message: 'Repeat count must be 1 or more.',
      });
    }
    if (until !== undefined && !DATE_PATTERN.test(until)) {
      problems.push({
        field: 'recurrence.until',
        code: 'recurrence-invalid',
        message: 'Repeat end date must look like 2026-08-15.',
      });
    }
  }

  return problems;
}

export function isExportable(draft: EventDraft): boolean {
  return validateEvent(draft).length === 0;
}
